import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency } from '../services';
import { careHoverStore } from '../services/careHoverStore';
import {
    CARD_MAX_WIDTH, SORT_OPTIONS,
    computePlacement, normalizeProcedures, sortProcedures, filterProcedures,
    groupProceduresByCode, sumFees, getUnitemizedAmount
} from '../services/careHover';

const OPEN_DELAY_MS = 120;     // pointer must rest briefly before a card opens (no flash while sweeping the table)
const SWITCH_DELAY_MS = 60;    // moving between two Care cells while a card is open
const CLOSE_GRACE_MS = 220;    // time allowed to cross the gap from the trigger to the card
const FILTER_THRESHOLD = 15;   // show the search box only for long lists
const ANCHOR_CHECK_MS = 400;

const EMPTY_VIEW = { sort: SORT_OPTIONS.original, group: false, query: '' };

/**
 * Cheap trigger rendered inside each day row. It owns no popup logic: it only tells the shared
 * CareHoverCardHost what to show and which element to anchor to.
 */
export const CareHoverTrigger = memo(({ children, title, procedures, total }) => {
    const ref = useRef(null);
    const payload = (source) => ({ anchorEl: ref.current, data: { title, procedures, total }, source });

    return (
        <span
            ref={ref}
            className="care-hover-trigger"
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            title="Hover to preview, click to pin the procedure list"
            onMouseEnter={() => careHoverStore.open(payload('hover'))}
            onMouseLeave={() => careHoverStore.close(ref.current, 'hover')}
            onFocus={() => careHoverStore.open(payload('focus'))}
            onBlur={() => careHoverStore.close(ref.current, 'focus')}
            onClick={() => careHoverStore.togglePin(payload('click'))}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    careHoverStore.togglePin(payload('click'));
                }
            }}
        >
            {children}
        </span>
    );
});

const rectOf = (el) => {
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, right: r.right, bottom: r.bottom };
};

/**
 * ONE hover card for the whole report table (mount once). Hover opens it with intent delays,
 * click / Enter pins it so a long list can be scrolled and filtered, Esc or an outside click
 * dismisses it. Placement is computed once per open (never re-flips under the pointer).
 */
export const CareHoverCardHost = () => {
    const [active, setActive] = useState(null);       // { anchorEl, data, pinned }
    const [placement, setPlacement] = useState(null);
    const [view, setView] = useState(EMPTY_VIEW);

    const cardRef = useRef(null);
    const activeRef = useRef(null);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const overCard = useRef(false);
    // true while a toolbar control is actively in use (native <select> list open, or typing in the filter box)
    const holdOpen = useRef(false);

    activeRef.current = active;

    const clearTimers = () => {
        clearTimeout(openTimer.current);
        clearTimeout(closeTimer.current);
        openTimer.current = null;
        closeTimer.current = null;
    };

    const closeNow = useCallback(() => {
        clearTimers();
        overCard.current = false;
        holdOpen.current = false;
        setActive(null);
        setPlacement(null);
    }, []);

    const openFor = useCallback((anchorEl, data, pinned) => {
        clearTimeout(closeTimer.current);
        holdOpen.current = false;
        setPlacement(null);
        setView(EMPTY_VIEW);
        setActive({ anchorEl, data, pinned });
    }, []);

    const scheduleClose = useCallback(() => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => {
            if (!overCard.current && !holdOpen.current && !(activeRef.current && activeRef.current.pinned)) closeNow();
        }, CLOSE_GRACE_MS);
    }, [closeNow]);

    // --- requests from the triggers ------------------------------------------------------------
    useEffect(() => careHoverStore.subscribe((evt) => {
        const current = activeRef.current;
        if (evt.type === 'open') {
            if (!evt.anchorEl) return;
            if (current && current.pinned) return;               // a pinned card is never hijacked by hover
            clearTimeout(closeTimer.current);
            if (current && current.anchorEl === evt.anchorEl) { clearTimeout(openTimer.current); return; }
            clearTimeout(openTimer.current);
            const delay = evt.source === 'focus' ? 0 : (current ? SWITCH_DELAY_MS : OPEN_DELAY_MS);
            openTimer.current = setTimeout(() => openFor(evt.anchorEl, evt.data, false), delay);
        } else if (evt.type === 'close') {
            clearTimeout(openTimer.current);
            openTimer.current = null;
            if (current && !current.pinned && current.anchorEl === evt.anchorEl) scheduleClose();
        } else if (evt.type === 'togglePin') {
            if (!evt.anchorEl) return;
            clearTimeout(openTimer.current);
            if (current && current.anchorEl === evt.anchorEl && current.pinned) {
                closeNow();
            } else if (current && current.anchorEl === evt.anchorEl) {
                setActive({ ...current, pinned: true });
            } else {
                openFor(evt.anchorEl, evt.data, true);
            }
        }
    }), [closeNow, openFor, scheduleClose]);

    // --- measure once, then lock placement -------------------------------------------------------
    const reposition = useCallback(() => {
        const current = activeRef.current;
        const card = cardRef.current;
        if (!current || !card || !current.anchorEl) return;
        const anchor = rectOf(current.anchorEl);
        const dpr = window.devicePixelRatio || 1;
        const size = card.getBoundingClientRect();
        setPlacement(computePlacement({
            anchor,
            card: { width: Math.min(CARD_MAX_WIDTH, size.width || CARD_MAX_WIDTH), height: card.scrollHeight || size.height },
            viewport: { width: document.documentElement.clientWidth, height: window.innerHeight },
            dpr
        }));
    }, []);

    useLayoutEffect(() => {
        if (active && !placement) reposition();
    }, [active, placement, reposition]);

    // --- global dismissal + scroll/resize handling ---------------------------------------------
    useEffect(() => {
        if (!active) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                const anchor = activeRef.current && activeRef.current.anchorEl;
                const wasPinned = activeRef.current && activeRef.current.pinned;
                closeNow();
                if (wasPinned && anchor && anchor.isConnected) anchor.focus();
            }
        };
        const onMouseDown = (e) => {
            const current = activeRef.current;
            if (!current || !(current.pinned || holdOpen.current)) return;
            const card = cardRef.current;
            if ((card && card.contains(e.target)) || (current.anchorEl && current.anchorEl.contains(e.target))) return;
            closeNow();
        };
        const onScroll = (e) => {
            const card = cardRef.current;
            if (card && e.target && card.contains(e.target)) return;   // scrolling the list itself
            const current = activeRef.current;
            if (current && current.pinned && current.anchorEl && current.anchorEl.isConnected) {
                setPlacement(null);   // re-measure against the anchor's new position
            } else {
                closeNow();
            }
        };
        const onResize = () => {
            const current = activeRef.current;
            if (current && current.pinned) setPlacement(null); else closeNow();
        };
        const anchorWatch = setInterval(() => {
            const current = activeRef.current;
            if (current && (!current.anchorEl || !current.anchorEl.isConnected)) closeNow();
        }, ANCHOR_CHECK_MS);

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onMouseDown, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            clearInterval(anchorWatch);
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onMouseDown, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [active, closeNow]);

    useEffect(() => () => clearTimers(), []);

    // --- content -----------------------------------------------------------------------------------
    const procedures = useMemo(() => normalizeProcedures(active?.data?.procedures), [active]);
    const rows = useMemo(() => {
        const filtered = filterProcedures(procedures, view.query);
        const base = view.group ? groupProceduresByCode(filtered) : filtered;
        return sortProcedures(base, view.sort);
    }, [procedures, view]);

    // Controls hold the card open only while in use; once released, normal pointer-leave closing resumes.
    const holdControl = () => { holdOpen.current = true; clearTimeout(closeTimer.current); };
    const releaseControl = () => {
        holdOpen.current = false;
        if (!overCard.current && !activeRef.current?.pinned) scheduleClose();
    };

    if (!active) return null;

    const dayTotal = active.data?.total;
    const itemizedTotal = sumFees(procedures);
    const unitemized = dayTotal == null ? 0 : getUnitemizedAmount(dayTotal, procedures);
    const shownTotal = sumFees(rows);
    const isFiltered = !!view.query.trim();
    const showToolbar = procedures.length > 1;

    const style = placement
        ? {
            position: 'fixed',
            left: placement.left,
            width: placement.width,
            maxHeight: placement.maxHeight,
            ...(placement.placement === 'top'
                // anchored by its bottom edge so it stays glued to the trigger if the list shrinks
                ? { bottom: window.innerHeight - (placement.top + placement.maxHeight) }
                : { top: placement.top })
        }
        : { position: 'fixed', left: 0, top: 0, width: CARD_MAX_WIDTH, maxWidth: 'calc(100vw - 16px)', visibility: 'hidden' };

    return createPortal(
        <div
            ref={cardRef}
            className={`care-hover-card${active.pinned ? ' is-pinned' : ''}`}
            role={active.pinned ? 'dialog' : 'tooltip'}
            aria-label={`Procedures for ${active.data?.title || 'day'}`}
            style={style}
            onMouseEnter={() => { overCard.current = true; clearTimeout(closeTimer.current); }}
            onMouseLeave={() => { overCard.current = false; if (!activeRef.current?.pinned) scheduleClose(); }}
        >
            <div className="care-hover-card__header">
                <div>
                    <strong>{active.data?.title ? `Care · ${active.data.title}` : 'Care'}</strong>
                    <div className="care-hover-card__sub">
                        {procedures.length} procedure{procedures.length === 1 ? '' : 's'} · {formatCurrency(dayTotal ?? itemizedTotal)}
                    </div>
                </div>
                {active.pinned
                    ? <button type="button" className="care-hover-card__close" aria-label="Close" onClick={closeNow}>×</button>
                    : <span className="care-hover-card__hint">Click to pin</span>}
            </div>

            {unitemized !== 0 && (
                <div className="care-hover-card__notice">
                    {formatCurrency(Math.abs(unitemized))} of this day's Care {unitemized > 0 ? 'has no procedure code and is not itemized below' : 'is itemized below but not in the day total'}.
                </div>
            )}

            {showToolbar && (
                <div className="care-hover-card__toolbar">
                    <select
                        aria-label="Sort procedures"
                        value={view.sort}
                        onMouseDown={holdControl}
                        onFocus={holdControl}
                        onBlur={releaseControl}
                        onChange={(e) => {
                            const sort = e.target.value;
                            setView(v => ({ ...v, sort }));
                            e.target.blur();   // choosing an option ends the interaction
                        }}
                    >
                        <option value={SORT_OPTIONS.original}>Original order</option>
                        <option value={SORT_OPTIONS.feeDesc}>Largest fee first</option>
                        <option value={SORT_OPTIONS.code}>Code A-Z</option>
                    </select>
                    <label className="care-hover-card__group">
                        <input
                            type="checkbox"
                            checked={view.group}
                            onChange={(e) => {
                                const group = e.target.checked;
                                setView(v => ({ ...v, group }));
                                e.target.blur();
                            }}
                        />
                        Group by code
                    </label>
                    {procedures.length > FILTER_THRESHOLD && (
                        <input
                            type="search"
                            className="care-hover-card__search"
                            placeholder="Filter code or description"
                            aria-label="Filter procedures"
                            value={view.query}
                            onFocus={holdControl}
                            onBlur={releaseControl}
                            onChange={(e) => { const query = e.target.value; setView(v => ({ ...v, query })); }}
                        />
                    )}
                </div>
            )}

            <div className="care-hover-card__body">
                {procedures.length === 0 ? (
                    <div className="care-hover-card__empty">No procedure data available</div>
                ) : rows.length === 0 ? (
                    <div className="care-hover-card__empty">No procedures match "{view.query}"</div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Description</th>
                                {view.group && <th className="is-num">Qty</th>}
                                <th className="is-num">Fee</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.key}>
                                    <td className="is-code">{r.code}</td>
                                    <td className="is-desc" title={r.description}>{r.description}</td>
                                    {view.group && <td className="is-num">{r.count}</td>}
                                    <td className="is-num">{formatCurrency(r.fee)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="care-hover-card__footer">
                <span>{isFiltered ? `Filtered (${rows.length})` : 'Total'}</span>
                <span>{formatCurrency(isFiltered ? shownTotal : (dayTotal ?? itemizedTotal))}</span>
            </div>
        </div>,
        document.body
    );
};
